import React, { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { PORT_FEATURES, PORT_STATUS, portCounts } from '../data/portStatus';
import { PortPill } from '../components/PortStatusBadge';

/**
 * Every admin portal feature and how far it has moved to CPX (Azure exit WS8).
 *
 * Features that are not ported have no CPX page to carry a badge, so this is the one
 * place staff can see them. It reads src/data/portStatus.json and nothing else.
 */

const FILTERS = [['all', 'All'], ...Object.entries(PORT_STATUS).map(([k, v]) => [k, v.label])];

export default function PortStatus({ navigateTo }) {
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const counts = portCounts();

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = PORT_FEATURES.filter((f) => filter === 'all' || f.status === filter)
      .filter((f) => !q || `${f.area} ${f.label} ${f.remaining || ''}`.toLowerCase().includes(q));
    const byArea = new Map();
    rows.forEach((f) => {
      if (!byArea.has(f.area)) byArea.set(f.area, []);
      byArea.get(f.area).push(f);
    });
    return [...byArea.entries()];
  }, [filter, query]);

  return (
    <div>
      <div className="page-header">
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Portal Move Status</h2>
      </div>
      <p className="port-intro">
        Every feature of the admin portal and how far it has moved here. Keep using the admin portal for anything
        listed under "Still in the admin portal". Fully ported means a parity test has passed.
      </p>
      <div className="port-tiles">
        {Object.entries(PORT_STATUS).map(([k, v]) => (
          <button type="button" key={k} className={`port-tile${filter === k ? ' on' : ''}`} onClick={() => setFilter(filter === k ? 'all' : k)}>
            <span className="port-tile-num">{counts[k]}</span>
            <span>{v.label}</span>
          </button>
        ))}
      </div>
      <div className="port-filters">
        <select className="form-select" value={filter} onChange={(e) => setFilter(e.target.value)}>
          {FILTERS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
        <div className="port-search">
          <Search size={13} />
          <input className="form-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search features..." />
        </div>
      </div>
      {groups.length === 0 && <p className="port-intro">No features match.</p>}
      {groups.map(([area, rows]) => (
        <div className="card port-card" key={area}>
          <div className="card-header">{area} <span className="port-area-count">{rows.length}</span></div>
          <div className="port-scroll">
            <table className="trans-table port-table">
              <thead>
                <tr><th>Feature</th><th>Status</th><th>In CPX</th><th>Still in the admin portal</th></tr>
              </thead>
              <tbody>
                {rows.map((f) => (
                  <tr key={f.id}>
                    <td>{f.label}</td>
                    <td><PortPill status={f.status} /></td>
                    <td>
                      {f.cpxPages.map((p) => (
                        <button type="button" key={p} className="port-link" onClick={() => navigateTo(p)}>{p.replace(/-/g, ' ')}</button>
                      ))}
                      {f.cpxPages.length === 0 && <span className="port-none">No page yet</span>}
                    </td>
                    <td>
                      {f.remaining}
                      {f.viaApp01 && f.viaApp01.length > 0 && (
                        <div className="port-note">Works in CPX, still runs on the old server: {f.viaApp01.join(', ')}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
